import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  useGetList,
  useLocaleState,
  useTranslate,
  type Identifier,
} from "ra-core";
import { ScheduleXCalendar, useNextCalendarApp } from "@schedule-x/react";
import {
  createViewDay,
  createViewMonthGrid,
  createViewWeek,
  type CalendarConfig,
  type CalendarEvent,
} from "@schedule-x/calendar";
import "@schedule-x/theme-default/dist/index.css";

import { useTheme } from "@/components/admin/use-theme";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router";
import type { Appointment, DevTask, Sale, Task } from "../types";
import { AppointmentCreateSheet } from "./AppointmentCreateSheet";
import { AppointmentEditSheet } from "./AppointmentEditSheet";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  ExternalLink,
  Calendar as CalIcon,
  MapPin,
  Clock,
  User as UserIcon,
  CheckCircle2,
} from "lucide-react";
import { MarkDoneDialog, type MarkDoneKind } from "../misc/MarkDoneDialog";
import { usePageMode } from "@/hooks/usePageMode";
import { AppointmentPageHead, type CalendarView } from "./AppointmentPageHead";
import { AppointmentAgentView } from "./AppointmentAgentView";
import {
  AppointmentToolbar,
  type AppointmentTypeFilter,
} from "./AppointmentToolbar";
import { AppointmentRail } from "./AppointmentRail";

// Fixed palette, cycled by sales id for deterministic per-user colors.
const USER_PALETTE = [
  { main: "#2563eb", light: "#dbeafe", dark: "#1e3a8a" }, // blue
  { main: "#dc2626", light: "#fee2e2", dark: "#7f1d1d" }, // red
  { main: "#16a34a", light: "#dcfce7", dark: "#14532d" }, // green
  { main: "#ea580c", light: "#ffedd5", dark: "#7c2d12" }, // orange
  { main: "#9333ea", light: "#f3e8ff", dark: "#581c87" }, // violet
  { main: "#0891b2", light: "#cffafe", dark: "#164e63" }, // cyan
  { main: "#db2777", light: "#fce7f3", dark: "#831843" }, // pink
  { main: "#ca8a04", light: "#fef9c3", dark: "#713f12" }, // amber
];

function colorForSaleId(id: number | string | null | undefined) {
  if (id == null) return USER_PALETTE[0];
  const n = typeof id === "number" ? id : Number(id);
  return USER_PALETTE[Math.abs(n) % USER_PALETTE.length];
}

function userCalendarId(saleId: number | string | null | undefined) {
  return saleId != null ? `user-${saleId}` : "user-none";
}

type CalendarTimeZone = NonNullable<CalendarConfig["timezone"]>;

const DEFAULT_APPOINTMENT_START_HOUR = 9;
const DEFAULT_APPOINTMENT_DURATION_HOURS = 1;

const statusColors: Record<string, string> = {
  scheduled: "#22c55e",
  completed: "#3b82f6",
  cancelled: "#ef4444",
  task: "#f59e0b",
  dev_task: "#a855f7",
};

const getCalendarTimeZone = (): CalendarTimeZone =>
  (Intl.DateTimeFormat().resolvedOptions().timeZone ||
    "UTC") as CalendarTimeZone;

const getCalendarLocale = (locale: string): string =>
  locale.startsWith("fr") ? "fr-FR" : "en-US";

const appointmentDateTimeFromIso = (
  iso: string,
  timeZone: CalendarTimeZone,
): Temporal.ZonedDateTime =>
  Temporal.Instant.from(iso).toZonedDateTimeISO(timeZone);

const toIsoString = (dateTime: Temporal.ZonedDateTime): string =>
  dateTime.toInstant().toString();

const getDefaultDateRange = (
  date: Temporal.PlainDate,
  timeZone: CalendarTimeZone,
) => {
  const start = Temporal.ZonedDateTime.from({
    year: date.year,
    month: date.month,
    day: date.day,
    hour: DEFAULT_APPOINTMENT_START_HOUR,
    timeZone,
  });
  const end = start.add({ hours: DEFAULT_APPOINTMENT_DURATION_HOURS });

  return {
    start: toIsoString(start),
    end: toIsoString(end),
  };
};

// Sprint 8.1 — Wrapper qui orchestre D1 (PageHead enrichi + Toolbar + Calendar
// Schedule-X + Right rail) vs D2 (EmbeddedAgentView). Conforme à
// cal-direction1.jsx Main : pagehead + toolbar + body-row (week-grid + rail).
//
// Note Sprint 8.x : abandon Schedule-X reporté — le custom WeekGrid décrit
// dans SPRINT_8_STRATEGIC_PLAN.md §6 sera implémenté plus tard. Le toggle
// agent + la nouvelle UI autour de Schedule-X apportent la valeur immédiate.
export const AppointmentList = () => {
  const [mode, , toggleMode] = usePageMode("appointments");
  const [view, setView] = useState<CalendarView>("week");
  const [mineOnly, setMineOnly] = useState(false);
  const [typeFilters, setTypeFilters] = useState<Set<AppointmentTypeFilter>>(
    () => new Set(["client", "interne"]),
  );
  const [hiddenSaleIds, setHiddenSaleIds] = useState<Set<number>>(new Set());

  const handleCreate = () => {
    window.dispatchEvent(new CustomEvent("atomic-calendar:create-rdv"));
  };

  const toggleSale = (id: number) => {
    setHiddenSaleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleType = (t: AppointmentTypeFilter) => {
    setTypeFilters((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  return (
    <div className="w-full flex flex-col gap-4">
      <AppointmentPageHead
        mode={mode}
        onToggleMode={toggleMode}
        view={view}
        onViewChange={setView}
        onCreate={handleCreate}
      />
      {mode === "agent" ? (
        <AppointmentAgentView />
      ) : (
        <>
          <AppointmentToolbar
            hiddenSaleIds={hiddenSaleIds}
            onToggleSale={toggleSale}
            mineOnly={mineOnly}
            onMineOnlyToggle={() => setMineOnly((m) => !m)}
            typeFilters={typeFilters}
            onTypeToggle={toggleType}
          />
          <div className="flex flex-col xl:flex-row gap-4">
            <div className="flex-1 min-w-0">
              <AppointmentCalendarView
                externalHiddenSaleIds={hiddenSaleIds}
                onExternalCreateRequest={() => undefined}
              />
            </div>
            <AppointmentRail />
          </div>
        </>
      )}
    </div>
  );
};

interface AppointmentCalendarViewProps {
  externalHiddenSaleIds?: Set<number>;
  onExternalCreateRequest?: () => void;
}

const AppointmentCalendarView = ({
  externalHiddenSaleIds,
}: AppointmentCalendarViewProps = {}) => {
  const translate = useTranslate();
  const [createOpen, setCreateOpen] = useState(false);
  const [editId, setEditId] = useState<string | number | null>(null);
  const [defaultStart, setDefaultStart] = useState<string | undefined>();
  const [defaultEnd, setDefaultEnd] = useState<string | undefined>();
  const [locale = "en"] = useLocaleState();
  const { theme } = useTheme();
  // ponytail: mirrors ThemeProvider (which also only samples matchMedia on theme change).
  const isDarkMode =
    theme === "dark" ||
    (theme === "system" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);
  const timeZone = useMemo(() => getCalendarTimeZone(), []);
  const calendarLocale = useMemo(() => getCalendarLocale(locale), [locale]);

  const navigate = useNavigate();

  const { data: sales } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "last_name", order: "ASC" },
  });

  // Sprint 8.1 — externalHiddenSaleIds passé par AppointmentList parent (nouveau
  // toolbar). Si présent, prend la priorité sur le state local (chips légacy).
  const [internalHiddenSaleIds, setHiddenSaleIds] = useState<Set<number>>(
    new Set(),
  );
  const hiddenSaleIds = externalHiddenSaleIds ?? internalHiddenSaleIds;

  type PopupSelection =
    | { kind: "appointment"; id: number }
    | { kind: "task"; id: number }
    | { kind: "devtask"; id: number }
    | null;
  const [popup, setPopup] = useState<PopupSelection>(null);

  const { data: appointments } = useGetList<Appointment>("appointments", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "start_at", order: "ASC" },
  });
  const { data: tasks } = useGetList<Task>("tasks", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "due_date", order: "ASC" },
    filter: { "done_date@is": null },
  });
  const { data: devTasks } = useGetList<DevTask>("dev_tasks", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "due_date", order: "ASC" },
    filter: { "due_date@not.is": null, "archived_at@is": null },
  });

  const events = useMemo<CalendarEvent[]>(() => {
    const appointmentEvents = (appointments ?? [])
      .filter((a) => !hiddenSaleIds.has(Number(a.sales_id)))
      .map<CalendarEvent>((a) => ({
        id: `appointment_${a.id}`,
        title: a.title,
        start: appointmentDateTimeFromIso(a.start_at, timeZone),
        end: appointmentDateTimeFromIso(a.end_at, timeZone),
        description: a.description ?? "",
        location: a.location ?? "",
        calendarId: userCalendarId(a.sales_id),
      }));
    const taskEvents = (tasks ?? [])
      .filter((t) => t.due_date && !hiddenSaleIds.has(Number(t.sales_id)))
      .map<CalendarEvent>((t) => {
        const start = appointmentDateTimeFromIso(t.due_date, timeZone);
        const end = start.add({ minutes: 30 });
        return {
          id: `task_${t.id}`,
          title: `📋 ${t.text ?? "Tâche"}`,
          start,
          end,
          description: t.text ?? "",
          location: "",
          calendarId: userCalendarId(t.sales_id),
        };
      });
    const devTaskEvents = (devTasks ?? [])
      .filter((t) => t.due_date)
      .flatMap<CalendarEvent>((t) => {
        const assignees = (
          (t.assignee_ids ?? []) as Array<number | string>
        ).map((n) => Number(n));
        const visibleAssignees = assignees.filter(
          (id) => !hiddenSaleIds.has(id),
        );
        if (assignees.length > 0 && visibleAssignees.length === 0) return [];
        const ownerSaleId = visibleAssignees[0] ?? assignees[0] ?? null;
        const d = new Date(t.due_date!);
        const iso = new Date(
          d.getFullYear(),
          d.getMonth(),
          d.getDate(),
          9,
          0,
          0,
        ).toISOString();
        const start = appointmentDateTimeFromIso(iso, timeZone);
        const end = start.add({ hours: 1 });
        return [
          {
            id: `devtask_${t.id}`,
            title: `🛠 ${t.title}`,
            start,
            end,
            description: t.description ?? "",
            location: "",
            calendarId: userCalendarId(ownerSaleId),
          },
        ];
      });
    return [...appointmentEvents, ...taskEvents, ...devTaskEvents];
  }, [appointments, tasks, devTasks, timeZone, hiddenSaleIds]);

  const views: CalendarConfig["views"] = [
    createViewMonthGrid(),
    createViewWeek(),
    createViewDay(),
  ];

  const calendar = useNextCalendarApp({
    views,
    // Sprint 8.1 — vue par défaut Semaine (cf maquette cal-direction1.jsx).
    // Avant : month-grid. Le segmented dans AppointmentPageHead permet de
    // switcher entre les 3 vues (month-grid/week/day) via CustomEvent
    // "atomic-calendar:set-view" écouté ci-dessous.
    defaultView: "week",
    locale: calendarLocale,
    timezone: timeZone,
    events,
    calendars: {
      scheduled: {
        colorName: "scheduled",
        lightColors: {
          main: statusColors.scheduled,
          container: "#dcfce7",
          onContainer: "#14532d",
        },
        darkColors: {
          main: statusColors.scheduled,
          container: "#14532d",
          onContainer: "#dcfce7",
        },
      },
      completed: {
        colorName: "completed",
        lightColors: {
          main: statusColors.completed,
          container: "#dbeafe",
          onContainer: "#1e3a8a",
        },
        darkColors: {
          main: statusColors.completed,
          container: "#1e3a8a",
          onContainer: "#dbeafe",
        },
      },
      cancelled: {
        colorName: "cancelled",
        lightColors: {
          main: statusColors.cancelled,
          container: "#fee2e2",
          onContainer: "#7f1d1d",
        },
        darkColors: {
          main: statusColors.cancelled,
          container: "#7f1d1d",
          onContainer: "#fee2e2",
        },
      },
      task: {
        colorName: "task",
        lightColors: {
          main: statusColors.task,
          container: "#fef3c7",
          onContainer: "#78350f",
        },
        darkColors: {
          main: statusColors.task,
          container: "#78350f",
          onContainer: "#fef3c7",
        },
      },
      dev_task: {
        colorName: "dev_task",
        lightColors: {
          main: statusColors.dev_task,
          container: "#f3e8ff",
          onContainer: "#581c87",
        },
        darkColors: {
          main: statusColors.dev_task,
          container: "#581c87",
          onContainer: "#f3e8ff",
        },
      },
      ...Object.fromEntries(
        (sales ?? []).map((s) => {
          const c = colorForSaleId(s.id);
          return [
            userCalendarId(s.id),
            {
              colorName: userCalendarId(s.id),
              lightColors: {
                main: c.main,
                container: c.light,
                onContainer: c.dark,
              },
              darkColors: {
                main: c.main,
                container: c.dark,
                onContainer: c.light,
              },
            },
          ];
        }),
      ),
      "user-none": {
        colorName: "user-none",
        lightColors: {
          main: "#64748b",
          container: "#e2e8f0",
          onContainer: "#1e293b",
        },
        darkColors: {
          main: "#94a3b8",
          container: "#334155",
          onContainer: "#f1f5f9",
        },
      },
    },
    callbacks: {
      onEventClick: (event) => {
        const id = String(event.id);
        if (id.startsWith("appointment_")) {
          setPopup({
            kind: "appointment",
            id: Number(id.slice("appointment_".length)),
          });
          return;
        }
        if (id.startsWith("devtask_")) {
          setPopup({
            kind: "devtask",
            id: Number(id.slice("devtask_".length)),
          });
          return;
        }
        if (id.startsWith("task_")) {
          setPopup({ kind: "task", id: Number(id.slice("task_".length)) });
          return;
        }
      },
      onClickDate: (date) => {
        const { start, end } = getDefaultDateRange(date, timeZone);
        setDefaultStart(start);
        setDefaultEnd(end);
        setCreateOpen(true);
      },
      onClickDateTime: (dateTime) => {
        setDefaultStart(toIsoString(dateTime));
        setDefaultEnd(
          toIsoString(
            dateTime.add({ hours: DEFAULT_APPOINTMENT_DURATION_HOURS }),
          ),
        );
        setCreateOpen(true);
      },
    },
  });

  // Refresh calendar events when data changes
  useEffect(() => {
    if (calendar) {
      calendar.events.set(events);
    }
  }, [events, calendar]);

  // Sprint 8.1 — Écouter le CustomEvent "atomic-calendar:set-view" dispatché
  // par le segmented de AppointmentPageHead. Permet de piloter Schedule-X
  // depuis un composant frère sans hoister tout le state.
  useEffect(() => {
    if (!calendar) return;
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ view: "month-grid" | "week" | "day" }>;
      try {
        calendar.calendarControls?.setView(ce.detail.view);
      } catch {
        /* schedule-x API absent */
      }
    };
    window.addEventListener("atomic-calendar:set-view", handler);
    return () =>
      window.removeEventListener("atomic-calendar:set-view", handler);
  }, [calendar]);

  const handleCreateClick = () => {
    setDefaultStart(undefined);
    setDefaultEnd(undefined);
    setCreateOpen(true);
  };

  // Sprint 8.1 — listener pour le bouton "Nouveau RDV" du AppointmentPageHead
  useEffect(() => {
    const handler = () => handleCreateClick();
    window.addEventListener("atomic-calendar:create-rdv", handler);
    return () =>
      window.removeEventListener("atomic-calendar:create-rdv", handler);
  }, []);

  // Sprint 8.1 — listener pour la nav prev/today/next du PageHead
  useEffect(() => {
    if (!calendar) return;
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ direction: "prev" | "today" | "next" }>;
      try {
        const ctrl = calendar.calendarControls;
        if (!ctrl) return;
        if (ce.detail.direction === "today") {
          const today = new Date().toISOString().slice(0, 10);
          ctrl.setDate(today);
        } else if (ce.detail.direction === "prev") {
          // schedule-x : getDate + reculer selon view
          const current = ctrl.getDate?.();
          if (!current) return;
          const d = new Date(current);
          const view = ctrl.getView?.() ?? "week";
          if (view === "day") d.setDate(d.getDate() - 1);
          else if (view === "week") d.setDate(d.getDate() - 7);
          else d.setMonth(d.getMonth() - 1);
          ctrl.setDate(d.toISOString().slice(0, 10));
        } else if (ce.detail.direction === "next") {
          const current = ctrl.getDate?.();
          if (!current) return;
          const d = new Date(current);
          const view = ctrl.getView?.() ?? "week";
          if (view === "day") d.setDate(d.getDate() + 1);
          else if (view === "week") d.setDate(d.getDate() + 7);
          else d.setMonth(d.getMonth() + 1);
          ctrl.setDate(d.toISOString().slice(0, 10));
        }
      } catch {
        /* schedule-x API mismatch */
      }
    };
    window.addEventListener("atomic-calendar:nav", handler);
    return () => window.removeEventListener("atomic-calendar:nav", handler);
  }, [calendar]);

  const toggleSale = (id: number) => {
    setHiddenSaleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <Card className="p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground mr-1">Équipe :</span>
          {(sales ?? []).map((s) => {
            const c = colorForSaleId(s.id);
            const hidden = hiddenSaleIds.has(Number(s.id));
            return (
              <Button
                key={s.id}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => toggleSale(Number(s.id))}
                className={cn(
                  "h-7 px-2 text-xs gap-1.5 border",
                  hidden && "opacity-40",
                )}
                style={{
                  borderColor: c.main,
                  color: hidden ? undefined : c.main,
                }}
              >
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: c.main }}
                />
                {s.first_name} {s.last_name}
              </Button>
            );
          })}
          {hiddenSaleIds.size > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setHiddenSaleIds(new Set())}
            >
              Tout afficher
            </Button>
          )}
        </div>
      </Card>
      <div
        className={cn(
          "calendar-wrapper rounded-lg border border-border bg-card overflow-hidden",
          "[&_.sx-react-calendar-wrapper]:h-full [&_.sx__calendar-wrapper]:h-full [&_.sx__calendar]:h-full",
          isDarkMode && "is-dark",
        )}
      >
        {/* Custom create button overlaid on calendar toolbar.
            The toolbar is ~72px tall (Schedule-X uses 48px round chevron
            buttons + padding), so align vertically with the toolbar row. */}
        <div className="pointer-events-none relative h-0">
          <div className="pointer-events-auto absolute right-3 top-4 z-20">
            <button
              type="button"
              onClick={handleCreateClick}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors"
            >
              <span className="text-base leading-none">+</span>
              {translate("resources.appointments.action.create", {
                _: "Événement",
              })}
            </button>
          </div>
        </div>
        <div className="h-[700px]">
          <ScheduleXCalendar calendarApp={calendar} />
        </div>
      </div>

      <AppointmentCreateSheet
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) {
            setDefaultStart(undefined);
            setDefaultEnd(undefined);
          }
        }}
        defaultStart={defaultStart}
        defaultEnd={defaultEnd}
      />

      {editId != null && (
        <AppointmentEditSheet
          open={editId != null}
          onOpenChange={(open) => {
            if (!open) setEditId(null);
          }}
          appointmentId={editId}
        />
      )}

      <EventPopup
        selection={popup}
        onClose={() => setPopup(null)}
        appointments={appointments ?? []}
        tasks={tasks ?? []}
        devTasks={devTasks ?? []}
        sales={sales ?? []}
        onEditAppointment={(id) => {
          setPopup(null);
          setEditId(String(id));
        }}
        onOpenDevTask={(id) => {
          setPopup(null);
          navigate(`/dev_tasks/${id}/show`);
        }}
        onOpenContact={(id) => {
          setPopup(null);
          navigate(`/contacts/${id}/show`);
        }}
      />
    </div>
  );
};

function EventPopup({
  selection,
  onClose,
  appointments,
  tasks,
  devTasks,
  sales,
  onEditAppointment,
  onOpenDevTask,
  onOpenContact,
}: {
  selection:
    | { kind: "appointment"; id: number }
    | { kind: "task"; id: number }
    | { kind: "devtask"; id: number }
    | null;
  onClose: () => void;
  appointments: Appointment[];
  tasks: Task[];
  devTasks: DevTask[];
  sales: Sale[];
  onEditAppointment: (id: number) => void;
  onOpenDevTask: (id: number) => void;
  onOpenContact: (id: number) => void;
}) {
  const salesById = new Map(sales.map((s) => [s.id, s]));
  const [doneTarget, setDoneTarget] = useState<{
    kind: MarkDoneKind;
    id: number;
    title: string;
    contactId?: number | null;
  } | null>(null);

  // ponytail: one view-model per kind, one render below.
  const describe = (): {
    icon: string;
    title: string;
    color: string;
    rows: Array<{ Icon: typeof Clock; node: ReactNode }>;
    badges: Array<{ text: string; variant: "secondary" | "outline" }>;
    description?: string | null;
    contactId?: Identifier | null;
    open?: { label: string; onClick: () => void };
    done?: Omit<NonNullable<typeof doneTarget>, "contactId"> & {
      contactId: number | null;
    };
  } | null => {
    if (!selection) return null;
    const saleRow = (saleId: Identifier | null | undefined) => {
      const sale = saleId ? salesById.get(saleId) : null;
      return sale
        ? [{ Icon: UserIcon, node: `${sale.first_name} ${sale.last_name}` }]
        : [];
    };
    if (selection.kind === "appointment") {
      const a = appointments.find((x) => x.id === selection.id);
      if (!a) return null;
      return {
        icon: "📅",
        title: a.title,
        color: colorForSaleId(a.sales_id).main,
        rows: [
          {
            Icon: Clock,
            node: `${new Date(a.start_at).toLocaleString("fr-FR")} → ${new Date(a.end_at).toLocaleString("fr-FR")}`,
          },
          ...(a.location ? [{ Icon: MapPin, node: a.location }] : []),
          ...saleRow(a.sales_id),
        ],
        badges: [
          ...(a.status
            ? [{ text: a.status, variant: "secondary" as const }]
            : []),
          ...(a.source
            ? [{ text: a.source, variant: "outline" as const }]
            : []),
        ],
        description: a.description,
        contactId: a.contact_id,
        open: {
          label: "Modifier",
          onClick: () => onEditAppointment(Number(a.id)),
        },
        done:
          a.status !== "completed"
            ? {
                kind: "appointment",
                id: Number(a.id),
                title: a.title,
                contactId: a.contact_id == null ? null : Number(a.contact_id),
              }
            : undefined,
      };
    }
    if (selection.kind === "task") {
      const t = tasks.find((x) => x.id === selection.id);
      if (!t) return null;
      return {
        icon: "📋",
        title: t.text ?? "Tâche",
        color: colorForSaleId(t.sales_id).main,
        rows: [
          {
            Icon: CalIcon,
            node: `Échéance : ${new Date(t.due_date).toLocaleString("fr-FR")}`,
          },
          ...saleRow(t.sales_id),
        ],
        badges:
          t.type && t.type !== "none"
            ? [{ text: t.type, variant: "secondary" }]
            : [],
        contactId: t.contact_id,
        done: {
          kind: "task",
          id: Number(t.id),
          title: t.text ?? "Tâche",
          contactId: Number(t.contact_id),
        },
      };
    }
    const t = devTasks.find((x) => x.id === selection.id);
    if (!t) return null;
    const assigneeIds = (t.assignee_ids ?? []) as Array<number | string>;
    const assignees = assigneeIds
      .map((aid) => salesById.get(Number(aid)))
      .filter((x): x is Sale => !!x);
    return {
      icon: "🛠",
      title: t.title,
      color: colorForSaleId(assigneeIds[0]).main,
      rows: [
        ...(t.due_date
          ? [
              {
                Icon: CalIcon,
                node: `Échéance : ${new Date(t.due_date).toLocaleDateString("fr-FR")}`,
              },
            ]
          : []),
        ...(assignees.length > 0
          ? [
              {
                Icon: UserIcon,
                node: assignees.map((a) => {
                  const c = colorForSaleId(a.id);
                  return (
                    <span
                      key={a.id}
                      className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full border"
                      style={{ borderColor: c.main, color: c.main }}
                    >
                      <span
                        className="inline-block w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: c.main }}
                      />
                      {a.first_name} {a.last_name}
                    </span>
                  );
                }),
              },
            ]
          : []),
      ],
      badges: [
        ...(t.status
          ? [{ text: t.status, variant: "secondary" as const }]
          : []),
        ...(t.priority && t.priority !== "none"
          ? [{ text: t.priority, variant: "outline" as const }]
          : []),
      ],
      description: t.description,
      open: {
        label: "Ouvrir le ticket",
        onClick: () => onOpenDevTask(Number(t.id)),
      },
      done:
        t.status !== "done"
          ? {
              kind: "devtask",
              id: Number(t.id),
              title: t.title,
              contactId: (t.contact_id as number | null) ?? null,
            }
          : undefined,
    };
  };

  const view = describe();

  return (
    <>
      <Dialog
        open={!!selection}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <DialogContent className="sm:max-w-lg">
          {view && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: view.color }}
                  />
                  <DialogTitle className="text-left flex-1">
                    {view.icon} {view.title}
                  </DialogTitle>
                </div>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                {view.rows.map(({ Icon, node }, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 flex-wrap text-muted-foreground"
                  >
                    <Icon className="h-4 w-4" />
                    {typeof node === "string" ? <span>{node}</span> : node}
                  </div>
                ))}
                {view.badges.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {view.badges.map((b) => (
                      <Badge
                        key={b.text}
                        variant={b.variant}
                        className="text-[10px]"
                      >
                        {b.text}
                      </Badge>
                    ))}
                  </div>
                )}
                {view.description && (
                  <div className="bg-muted rounded-md p-2 whitespace-pre-wrap">
                    {view.description}
                  </div>
                )}
              </div>
              <DialogFooter>
                {view.contactId && (
                  <Button
                    variant="outline"
                    onClick={() => onOpenContact(Number(view.contactId))}
                  >
                    Fiche contact
                  </Button>
                )}
                {view.open && (
                  <Button onClick={view.open.onClick}>
                    <ExternalLink className="h-3.5 w-3.5 mr-1" />{" "}
                    {view.open.label}
                  </Button>
                )}
                {view.done && (
                  <Button
                    variant="default"
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => setDoneTarget(view.done ?? null)}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Fait
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      {doneTarget && (
        <MarkDoneDialog
          open={!!doneTarget}
          onOpenChange={(o) => {
            if (!o) setDoneTarget(null);
          }}
          kind={doneTarget.kind}
          id={doneTarget.id}
          title={doneTarget.title}
          contactId={doneTarget.contactId}
          onDone={() => {
            setDoneTarget(null);
            onClose();
          }}
        />
      )}
    </>
  );
}
